import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { CheckCircle2, Circle, ArrowRight, PartyPopper, Sparkles } from 'lucide-react';

const Welcome = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading, user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!loading && !isAuthenticated) navigate('/login');
  }, [loading, isAuthenticated, navigate]);

  const load = async () => {
    try {
      const res = await axios.get(`${API_URL}/onboarding/progress`);
      setData(res.data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    document.title = 'Welcome to INASW';
    load();
  }, []);

  const toggle = async (item) => {
    setBusy(item.id);
    try {
      await axios.post(`${API_URL}/onboarding/checklist/toggle`, { item_id: item.id, done: !item.done });
      await load();
    } catch (e) { /* ignore */ }
    setBusy(null);
  };

  if (!data) {
    return <div className="min-h-screen flex items-center justify-center pt-20 text-gray-500">Loading your welcome…</div>;
  }

  const doneCount = data.checklist.filter((c) => c.done).length;

  return (
    <div className="min-h-screen bg-[#F7F5EF] pt-20 pb-16 px-4" data-testid="welcome-page">
      <div className="max-w-2xl mx-auto">
        <div className="bg-primary rounded-3xl p-8 text-center text-white mb-6">
          <PartyPopper className="w-12 h-12 mx-auto text-secondary mb-3" />
          <h1 className="font-serif text-3xl font-bold" data-testid="welcome-title">{data.welcome_title}</h1>
          <p className="text-white/85 mt-3 max-w-lg mx-auto">{data.welcome_message}</p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-1">
            <h2 className="font-serif text-xl font-bold text-primary">Your Getting Started Checklist</h2>
            <span className="text-sm font-semibold text-gray-500" data-testid="welcome-checklist-count">{doneCount}/{data.checklist.length} done</span>
          </div>
          <div className="w-full h-2 bg-gray-100 rounded-full mb-4 overflow-hidden">
            <div className="h-full bg-secondary transition-all" style={{ width: `${data.checklist.length ? (doneCount / data.checklist.length) * 100 : 0}%` }} />
          </div>

          <ul className="space-y-3">
            {data.checklist.map((item) => (
              <li key={item.id} className="flex items-start gap-3 p-3 rounded-xl border border-gray-100 hover:border-secondary/40 transition-colors" data-testid={`welcome-item-${item.id}`}>
                <button onClick={() => toggle(item)} disabled={busy === item.id} className="mt-0.5 flex-shrink-0" aria-label="toggle" data-testid={`welcome-toggle-${item.id}`}>
                  {item.done ? <CheckCircle2 className="w-6 h-6 text-emerald-600" /> : <Circle className="w-6 h-6 text-gray-300" />}
                </button>
                <div className="flex-1">
                  <p className={`font-semibold ${item.done ? 'line-through text-gray-400' : 'text-gray-900'}`}>{item.label}</p>
                  <p className="text-sm text-gray-500">{item.description}</p>
                </div>
                <Link to={item.action_url} className="text-sm font-semibold text-primary hover:underline whitespace-nowrap flex items-center gap-1 mt-1">
                  {item.action_label} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </li>
            ))}
          </ul>

          {(!user || user.user_type === 'job_seeker') && (
            <Button
              onClick={() => navigate('/job-preferences?welcome=true')}
              className="w-full mt-6 h-11 bg-primary hover:bg-primary-hover text-white"
              data-testid="welcome-set-preferences"
            >
              <Sparkles className="w-4 h-4 mr-1" /> Set My Job Preferences
            </Button>
          )}
          <Button
            onClick={() => navigate(data.dashboard_path || '/dashboard')}
            className={`w-full mt-3 h-11 ${(!user || user.user_type === 'job_seeker') ? 'bg-white text-primary border border-primary hover:bg-primary/5' : 'bg-secondary hover:bg-secondary-hover text-white'}`}
            data-testid="welcome-go-dashboard"
          >
            Go to My Dashboard <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Welcome;
